import type { ChangeEvent, Ref, RefObject } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import clsx from 'clsx';
import { v4 } from 'uuid';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import FileList from './file-list.component';
import ErrorLabel from '../../labels/error-label';
import { useTranslation } from 'react-i18next';
import { mergeStyling } from '../../common-functions';
import Dialog from '../../modal-dialog/dialog.component';

export interface FileUploadProps extends DynamicFieldComponentProps<Array<File>> {
  maxUploadFiles?: number;
  maxUploadFileSize?: number;
  allowedFileTypes?: Array<string>;
  labelStyle?: string;
  classNames?: string;
  thumbnails?: boolean;
  containerStyles?: string;
  isUploading?: boolean;
  accept?: string;
  children?: (args: {
    files: File[];
    removeFile: (file: File) => void;
    isUploading?: boolean;
    dropElement: RefObject<HTMLDivElement>;
  }) => React.ReactNode;
}

export interface FileRef {
  resetFiles: () => void;
}

const defaultMaxUploadFileSize = 10485760; // equal to 10 MB
// Invalid filename characters (Windows & cross-platform restrictions). Forward slash does not need escaping here; only backslash is escaped.
const invalidPattern = /[<>:"/\\|?*]/g;

function FileUpload(props: FileUploadProps, ref?: Ref<FileRef>) {
  const {
    maxUploadFiles = 5,
    maxUploadFileSize = defaultMaxUploadFileSize,
    allowedFileTypes,
    label,
    readOnly,
    required,
    value,
    error,
    onChange,
    labelStyle = 'odin-font-bold odin-text-sm',
    classNames,
    thumbnails = false,
    containerStyles,
    isUploading,
    accept = '*',
  }: FileUploadProps = props;
  const [files, setFiles] = useState<Array<File>>(value || []);
  const [errorMessage, setErrorMessage] = useState(error);
  const [modalVisible, setModalVisible] = useState(false);

  const guid = useRef(v4());
  const dropElement = useRef<HTMLDivElement>(null);
  const maxFileLimitReached = useRef(false);
  const invalidFileTypeList = useRef<string[]>([]);
  const invalidMaxFileSizeList = useRef<string[]>([]);
  const invalidEmptyFileList = useRef<string[]>([]);
  const invalidFileName = useRef<string[]>([]);

  const { t } = useTranslation();

  const resetFiles = () => {
    resetValidationState();
    setFiles([]);
  };

  useImperativeHandle(ref, () => ({
    resetFiles,
  }));

  const validateFilename = useCallback(
    (filename: File) => {
      const invalidChars = filename.name.match(invalidPattern);

      if (invalidChars) {
        invalidFileName.current.push(t('file-upload-name-error', { invalidChars: [...new Set(invalidChars)] }));
        return false;
      }
      return true;
    },
    [t]
  );

  const validateFile = useCallback(
    (droppedFile: File) => {
      let valid = true;

      if (droppedFile.size < 1) {
        valid = false;
        invalidEmptyFileList.current.push(droppedFile.name);
      }

      if (droppedFile.size > maxUploadFileSize) {
        valid = false;
        invalidMaxFileSizeList.current.push(droppedFile.name);
      }

      if (allowedFileTypes) {
        const extension = droppedFile.name.split('.').pop();
        if (extension && !allowedFileTypes.includes(extension)) {
          valid = false;
          invalidFileTypeList.current.push(droppedFile.name);
        }
      }

      if (!validateFilename(droppedFile)) {
        valid = false;
      }
      return valid;
    },
    [allowedFileTypes, maxUploadFileSize, validateFilename]
  );

  const resetValidationState = useCallback(() => {
    maxFileLimitReached.current = false;
    invalidEmptyFileList.current = [];
    invalidFileTypeList.current = [];
    invalidMaxFileSizeList.current = [];
    invalidFileName.current = [];
  }, []);

  const hasInvalidFiles = useCallback(() => {
    if (
      invalidEmptyFileList.current.length > 0 ||
      invalidFileTypeList.current.length > 0 ||
      invalidMaxFileSizeList.current.length > 0 ||
      invalidFileName.current.length > 0
    ) {
      return true;
    }
    return false;
  }, []);

  const updateUploadedFiles = useCallback(
    (uploadedFiles: FileList) => {
      const currentFiles = [...files];
      let uploadedFileArray = Array.from(uploadedFiles);

      // Validate
      resetValidationState();

      if (files.length + uploadedFiles.length > maxUploadFiles) {
        maxFileLimitReached.current = true;
        setModalVisible(true);
      } else {
        uploadedFileArray = uploadedFileArray.filter(validateFile);

        // if there are files already uploaded then check for duplicates
        if (files.length > 0) {
          uploadedFileArray = uploadedFileArray.filter(
            (file) =>
              !currentFiles.find(
                (currentFile) => currentFile.name === file.name && currentFile.lastModified === file.lastModified
              )
          );
        }

        if (uploadedFileArray.length > 0) {
          currentFiles.push(...uploadedFileArray);
          setFiles(currentFiles);
        }

        onChange?.(currentFiles);
        setModalVisible(hasInvalidFiles());
      }
    },
    [files, validateFile, onChange, hasInvalidFiles, maxUploadFiles, resetValidationState]
  );

  useEffect(() => {
    if (!dropElement.current) return;

    const onFilesDropped = (event: DragEvent) => {
      if (event.dataTransfer?.files) {
        updateUploadedFiles(event.dataTransfer.files);
      }
    };

    const preventDefault = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const dragEvents: Array<'dragenter' | 'dragleave' | 'dragover' | 'drop'> = [
      'dragenter',
      'dragleave',
      'dragover',
      'drop',
    ];

    const tempDropElement = dropElement.current;
    dragEvents.forEach((event) => {
      tempDropElement.addEventListener(event, preventDefault);
      if (event === 'drop') {
        tempDropElement.addEventListener(event, onFilesDropped);
      }
    });

    setErrorMessage(error || '');

    return () => {
      dragEvents.forEach((event) => tempDropElement.removeEventListener(event, preventDefault));
      tempDropElement.removeEventListener('drop', onFilesDropped);
    };
  }, [allowedFileTypes, dropElement, files.length, maxUploadFileSize, maxUploadFiles, updateUploadedFiles, error]);

  const onFilesChanged = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      updateUploadedFiles(event.target.files);
    }
  };

  const removeFile = (fileToRemove: File) => {
    let currentFiles = [...files];
    currentFiles = currentFiles.filter((file) => file.name !== fileToRemove.name);

    setFiles(currentFiles);
    onChange?.(currentFiles);
  };

  const onModalHidden = useCallback(() => {
    setModalVisible(false);
    resetValidationState();
  }, [resetValidationState]);

  const dropContainerStyles = clsx(
    'odin-flex odin-w-full odin-h-full odin-border-2 odin-border-dashed odin-min-h-[200px] odin-min-w-min odin-bg-primary-5 odin-font-bold odin-text-primary-2 odin-justify-center odin-items-start',
    { 'odin-items-center': files.length === 0 },
    { 'odin-border-primary-2': !errorMessage },
    { 'odin-border-error': errorMessage }
  );

  return (
    <>
      <div className={classNames}>
        {label && (
          <div className="odin-flex">
            <div className={labelStyle}>{label}</div>
            <RequiredIndicator readOnly={readOnly} required={required} />
          </div>
        )}
        {!readOnly && (
          <label className="odin-cursor-pointer" htmlFor={`file_${guid.current}`}>
            {typeof props.children === 'function' ? (
              props.children({ files, removeFile, isUploading, dropElement })
            ) : (
              <div className={mergeStyling(dropContainerStyles, containerStyles)} ref={dropElement}>
                {files.length === 0 ? (
                  <div className="odin-flex">{t('file-upload', 'Click to upload or drop files here')}</div>
                ) : (
                  <FileList files={files} thumbnails={thumbnails} onRemove={removeFile} isUploading={isUploading} />
                )}
              </div>
            )}
            <input
              type="file"
              accept={accept}
              className="odin-hidden"
              id={`file_${guid.current}`}
              multiple={maxUploadFiles > 1}
              onChange={onFilesChanged}
              onClick={(event) => (event.currentTarget.value = '')}
            />
          </label>
        )}
        {errorMessage && <ErrorLabel>{errorMessage}</ErrorLabel>}
      </div>
      {modalVisible && (
        <Dialog
          visible={modalVisible}
          onClose={onModalHidden}
          header={t('error', 'Error')}
          showCloseIcon={false}
          buttons={[{ name: 'ok', text: t('ok', 'Ok'), closeModalOnClick: true, type: 'primary' }]}
        >
          <div className="odin-leading-6 odin-px-4 odin-text-sm">
            {maxFileLimitReached.current ? (
              <span>
                {t('file-upload-limit', `You cannot upload more than ${(<strong>{maxUploadFiles}</strong>)} files.`, {
                  maxUploadFiles: maxUploadFiles,
                })}
              </span>
            ) : (
              <ul className="odin-list-disc">
                {invalidEmptyFileList.current.length > 0 && (
                  <li>
                    <strong>{invalidEmptyFileList.current.join(', ')}</strong>
                    {` ${t('file-upload-empty-file', 'are 0 bytes and will not be attached.')}`}
                  </li>
                )}
                {invalidFileTypeList.current.length > 0 && allowedFileTypes && (
                  <li>
                    <strong>{invalidFileTypeList.current.join(', ')}</strong>
                    {` ${t('file-upload-type-error', 'should be one of the following types:')} `}
                    <strong>{allowedFileTypes.join(', ')}</strong>.
                  </li>
                )}
                {invalidMaxFileSizeList.current.length > 0 && (
                  <li>
                    <strong>{invalidMaxFileSizeList.current.join(', ')}</strong>
                    {` ${t('file-upload-size-error', 'cannot be larger than')} `}
                    <strong>{maxUploadFileSize / 1024 / 1024} MB</strong>.
                  </li>
                )}
                {invalidFileName.current.length > 0 &&
                  invalidFileName.current.map((item, index) => {
                    return <li key={index}>{item}</li>;
                  })}
              </ul>
            )}
          </div>
        </Dialog>
      )}
    </>
  );
}

export default forwardRef(FileUpload);
